import { Grid } from '@mui/material';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useOutletContext } from 'react-router';

import OverviewTable from '../../../components/OverviewTable/OverviewTable';
import rbacCheck from '../../../rbac/rbacCheck';
import OrganizationBreadCrumb from '../OrganizationBreadCrumb';

import EditOrganizationFormModal from './EditOrganizationFormModal';
import MarketplaceItems from './MarketplaceItems';

import GitlabWikiHelpButton from '@/components/GitlabWikiHelpButton';
import type { Organization } from '@/types/organization';
import type { OverviewTableRow } from '@/types/tableTypes';
import type { User } from '@/types/user';

const usersGuideSlug = 'user-guide/organizations/overview';

interface RootState {
  user: User;
}

const Overview = (): React.ReactElement => {
  const user = useSelector((state: RootState) => state.user);

  const [organization, setOrganization] =
    useOutletContext<[Organization, (org: Organization) => void]>();
  const [editModalOpened, setEditModalOpened] = useState<boolean>(false);

  const isAdminOrg = organization.enterpriseName.trim().toLowerCase() === 'admin-org';

  const canSeeMarketplace: boolean = rbacCheck(user.roles, '/marketplace:VIEW') as boolean;
  const canActivateMarketplace: boolean = rbacCheck(user.roles, '/marketplace:PUT') as boolean;
  const canEditOrg: boolean = rbacCheck(user.roles, '/organization:PUT') as boolean;

  const rows: OverviewTableRow[] = [
    {
      title: 'CRM ID',
      value: organization.CRMId ?? '',
    },
    {
      title: 'Account owner',
      value: organization.accountOwner?.email ?? '',
      component: (
        <Link to={`/users/${organization.accountOwner?.uuid ?? ''}`}>
          {organization.accountOwner?.email}
        </Link>
      ),
    },
  ];

  return (
    <>
      {editModalOpened && (
        <EditOrganizationFormModal
          organization={organization}
          onSubmit={setOrganization}
          onCancel={() => {
            setEditModalOpened(false);
          }}
        />
      )}
      <OrganizationBreadCrumb />
      <Grid
        container
        direction='column'
        rowGap={8}
      >
        <Grid item>
          <OverviewTable
            header={organization.enterpriseName}
            rows={rows}
            editAction={{
              onClick: () => {
                setEditModalOpened(true);
              },
              disabled: !canEditOrg,
              tooltip: 'editOrganizationDisabled',
              label: '',
            }}
            trailingActionSlot={
              <GitlabWikiHelpButton
                slug={usersGuideSlug}
                tooltipMessageId='usersGuide'
                tooltipDefaultMessage='Users guide'
                modalTitleMessageId='usersGuide'
                modalTitleDefaultMessage='Users guide'
                buttonAriaLabel='Open users guide'
                buttonSx={{ mt: 0 }}
              />
            }
          />
        </Grid>
        {canSeeMarketplace && !isAdminOrg && (
          <Grid item>
            <MarketplaceItems
              organization={organization}
              setOrganization={setOrganization}
              canActivate={canActivateMarketplace}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default Overview;
